/**
 * Single-persona chat page (§7g — project-aware extension of the v1 Chat).
 *
 * Mounted at THREE routes:
 *   /coaches/chat/:personaId[/:conversationId]              — standalone
 *   /projects/:projectId/chat/:personaId[/:conversationId]  — project-scoped
 *   (/chat/... redirects to /coaches/chat/... via LegacyChatRedirect)
 *
 * The projectId URL param is optional. When present, it's passed to
 * streamChat() in the request body so the chat Lambda does dual RAG
 * retrieval (persona research + project documents) and tags the
 * conversation with the project. When absent, the chat Lambda treats
 * the request byte-identically to v1.
 *
 * After a new conversation is created, navigation preserves the
 * project context: project-scoped chats route to
 * /projects/:id/chat/:personaId/:newConvId, standalone chats to
 * /coaches/chat/:personaId/:newConvId.
 *
 * The budget footer polls /api/usage on mount and after every completed
 * turn. Budget-exhausted errors come through as an Error with `kind:
 * "budget"` which we render via the amber ErrorBubble variant.
 */

import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Header from "../components/Header.jsx";
import Sidebar from "../components/Sidebar.jsx";
import Message from "../components/Message.jsx";
import ToolActivity from "../components/ToolActivity.jsx";
import Avatar from "../components/Avatar.jsx";
import ErrorBubble from "../components/ErrorBubble.jsx";
import BudgetFooter from "../components/BudgetFooter.jsx";
import { useStore } from "../store/useStore.js";
import { useChatNavigation } from "../hooks/useChatNavigation.js";
import { streamChat } from "../services/chat.js";
import * as api from "../services/api.js";

export default function Chat() {
  const { personaId, conversationId, projectId } = useParams();
  const navigate = useNavigate();
  const { chatPath } = useChatNavigation(projectId);

  const personas = useStore((s) => s.personas);
  const loadPersonas = useStore((s) => s.loadPersonas);
  const getPersonaById = useStore((s) => s.getPersonaById);
  const messages = useStore((s) => s.activeMessages);
  const isStreaming = useStore((s) => s.isStreaming);
  const hydrateConversation = useStore((s) => s.hydrateActiveConversation);
  const appendUser = useStore((s) => s.appendUserMessage);
  const startAssistant = useStore((s) => s.startAssistantMessage);
  const appendToken = useStore((s) => s.appendAssistantToken);
  const finishStreaming = useStore((s) => s.finishStreaming);
  const clearActiveChat = useStore((s) => s.clearActiveChat);
  const appendToolCall = useStore((s) => s.appendToolCall);
  const resolveToolResult = useStore((s) => s.resolveToolResult);
  const upsertConversation = useStore((s) => s.upsertConversation);

  const [input, setInput] = useState("");
  const [error, setError] = useState(null);
  const [errorVariant, setErrorVariant] = useState("error");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const messagesEndRef = useRef(null);
  const justCreatedRef = useRef(null);
  // AbortController for the active SSE stream — aborted on unmount so
  // a stale fetch can't keep mutating store state after the component
  // is gone (rapid nav between chats / projects).
  const streamAbortRef = useRef(null);

  const persona = getPersonaById(personaId);

  // Load personas if not yet loaded
  useEffect(() => {
    loadPersonas();
  }, [loadPersonas]);

  // Load conversation messages when the URL conversationId changes.
  //
  // Uses a local `cancelled` flag so a rapid sequence of conversation
  // switches (e.g. keyboard-navigating through the sidebar) can't let
  // an earlier fetch's setMessages land after a later one — the last
  // navigation should win regardless of fetch latency.
  useEffect(() => {
    if (!conversationId) {
      clearActiveChat();
      return;
    }
    // Skip loading when we just created this conversation in-session
    if (conversationId === justCreatedRef.current) {
      justCreatedRef.current = null;
      return;
    }
    let cancelled = false;
    api
      .getConversation(conversationId)
      .then((conv) => {
        if (cancelled) return;
        hydrateConversation(conv);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [conversationId, clearActiveChat, hydrateConversation]);

  // Auto-scroll on new tokens
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Abort any in-flight stream on unmount so it can't keep writing to
  // activeMessages after the component is gone.
  useEffect(() => {
    return () => {
      streamAbortRef.current?.abort();
    };
  }, []);

  const conversationUrl = (convId) => chatPath(personaId, convId);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    // Read isStreaming from the store directly rather than the closure:
    // two handleSubmit calls fired in the same event-loop tick (Enter
    // auto-repeat) would both see the subscribed value as false until
    // React commits, letting both turns start simultaneously and
    // clobber streamAbortRef. The store's getState() is synchronous
    // and sees startAssistant()'s write from any earlier call in the
    // same tick, closing the race.
    if (!text || useStore.getState().isStreaming) return;

    setError(null);
    setErrorVariant("error");
    setInput("");
    appendUser(text);
    startAssistant();

    // Fresh abort controller per turn; signal lives on the ref so the
    // unmount effect can reach it.
    const controller = new AbortController();
    streamAbortRef.current = controller;

    await streamChat({
      personaId,
      conversationId,
      projectId,
      message: text,
      signal: controller.signal,
      onToken: (token) => appendToken(token),
      onToolCall: (data) => appendToolCall(data),
      onToolResult: (data) => resolveToolResult(data),
      onWarning: (data) => {
        setError(data.message);
        setErrorVariant("error");
      },
      onDone: (data) => {
        finishStreaming();
        if (data.conversationId && data.conversationId !== conversationId) {
          // Mark as "just created" so the URL-change effect skips re-fetch
          justCreatedRef.current = data.conversationId;
          upsertConversation({
            id: data.conversationId,
            personaId,
            projectId: projectId || null,
            title: data.title || text.slice(0, 60),
            updatedAt: new Date().toISOString(),
          });
          navigate(conversationUrl(data.conversationId), { replace: true });
        }
      },
      onError: (err) => {
        finishStreaming();
        setError(err.message);
        setErrorVariant(err.kind === "budget" ? "budget" : "error");
      },
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  // Show loading until personas resolve
  if (personas.length === 0) {
    return (
      <>
        <Header />
        <div className="loading">Loading…</div>
      </>
    );
  }

  if (!persona) {
    return (
      <>
        <Header />
        <div className="loading">Persona not found.</div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="chat-layout">
        <Sidebar
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          projectId={projectId || null}
        />
        <section className="chat-pane">
          <div className="chat-header">
            <button
              type="button"
              className="chat-menu-btn"
              onClick={() => setSidebarOpen(true)}
              aria-label="Open conversations"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </button>
            {projectId && (
              <Link
                to={`/projects/${projectId}`}
                className="chat-back-btn"
                aria-label="Back to project"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="19" y1="12" x2="5" y2="12" />
                  <polyline points="12 19 5 12 12 5" />
                </svg>
                <span>Project</span>
              </Link>
            )}
            <Avatar persona={persona} />
            <div className="chat-header-info">
              <h2>{persona.name}</h2>
              <span>{persona.category}</span>
            </div>
          </div>

          <div className="chat-messages">
            {messages.length === 0 && !isStreaming && (
              <ChatEmptyState persona={persona} onPrompt={(p) => setInput(p)} />
            )}
            {messages.map((m, i) =>
              m.role === "tool_activity" ? (
                <ToolActivity key={i} activities={m.activities} />
              ) : (
                <Message
                  key={i}
                  role={m.role}
                  content={m.content}
                  streaming={isStreaming && i === messages.length - 1}
                />
              ),
            )}
            {error && <ErrorBubble message={error} variant={errorVariant} />}
            <div ref={messagesEndRef} />
          </div>

          <div className="chat-input">
            <form onSubmit={handleSubmit}>
              <textarea
                rows={1}
                placeholder={`Message ${persona.name}…`}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isStreaming}
              />
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isStreaming || !input.trim()}
              >
                Send
              </button>
            </form>
            <BudgetFooter />
          </div>
        </section>
      </div>
    </>
  );
}

function ChatEmptyState({ persona, onPrompt }) {
  const frameworks = persona.frameworks || [];
  const prompts = frameworks.length > 0
    ? frameworks.slice(0, 3).map((f) => `Tell me about ${f.name}`)
    : [
        `What's your single biggest piece of advice?`,
        `How do you approach hard decisions?`,
        `What mistakes do most people make in your field?`,
      ];

  return (
    <div className="chat-empty">
      <div className="chat-empty-inner">
        <Avatar persona={persona} className="persona-avatar chat-empty-avatar" />
        <h3>Chat with {persona.name}</h3>
        <p>{persona.identity?.positioning || `Ask ${persona.name} anything to get started.`}</p>
        <div className="chat-empty-prompts">
          {prompts.map((p) => (
            <button
              key={p}
              type="button"
              className="chat-empty-prompt"
              onClick={() => onPrompt(p)}
            >
              {p}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
